// Pending Response — something's gone out to the customer, they haven't approved
// anything yet, and the job card isn't closed — the queue that flags a conversation
// nobody's followed up on. Same table + side-panel pattern as Escalations.jsx.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageCircle, MessageCircleQuestion, X } from "lucide-react";
import { NUM, CARD, formatWaited } from "../lib/dashboardTokens";
import { useDemoIdentity } from "../lib/DemoIdentityContext";
import { useGroups } from "../lib/api";
import { toQueueVehicle, scopeByWorkshop, filterByLocationParam, isPendingResponse, minsSince } from "../lib/queueMetrics";
import {
  rangeFromSearchParams,
  formatRangeChipLabel,
  parseCreatedAt,
  inRange,
  searchParamsWithoutLocation,
  searchParamsWithoutRange,
} from "../lib/dates";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { TablePagination } from "../components/TablePagination";

const PAGE_SIZE = 10;

export default function PendingResponse() {
  const navigate = useNavigate();
  const identity = useDemoIdentity();
  const { scopeLocations } = identity;
  const { data: groupsData, isLoading } = useGroups();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationFilter = searchParams.get("location");
  const dateRange = useMemo(() => rangeFromSearchParams(searchParams), [searchParams]);
  const dateLabel = dateRange ? formatRangeChipLabel(dateRange) : null;

  const scopedVehicles = useMemo(
    () => scopeByWorkshop((groupsData?.rows || []).map(toQueueVehicle), identity),
    [groupsData, identity]
  );
  const showWorkshop = !locationFilter && (scopeLocations === "all" || (Array.isArray(scopeLocations) && scopeLocations.length > 1));

  const items = useMemo(
    () =>
      filterByLocationParam(scopedVehicles, locationFilter)
        .filter((v) => isPendingResponse(v.row))
        .filter((v) => !dateRange || inRange(parseCreatedAt(v.row), dateRange))
        .map((v) => ({ ...v, waitedMins: minsSince(v.createdAt) }))
        .sort((a, b) => b.waitedMins - a.waitedMins),
    [scopedVehicles, locationFilter, dateRange]
  );

  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [items.length, identity, locationFilter, dateLabel]);
  const pageRows = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const [selectedId, setSelectedId] = useState(null);
  const selected = items.find((v) => v.id === selectedId) || null;

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="pending-response-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-work text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Pending Response</h1>
        <div className="flex flex-wrap items-center gap-2">
          {locationFilter && (
            <button
              type="button"
              onClick={() => setSearchParams(searchParamsWithoutLocation(searchParams))}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
              data-testid="location-filter-chip"
            >
              {locationFilter}
              <X size={12} />
            </button>
          )}
          {dateLabel && (
            <button
              type="button"
              onClick={() => setSearchParams(searchParamsWithoutRange(searchParams))}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
              data-testid="date-filter-chip"
            >
              {dateLabel}
              <X size={12} />
            </button>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
            <span className={NUM}>{items.length}</span> waiting
          </span>
        </div>
      </div>

      <div className={CARD} data-testid="pending-response-table-card">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-slate-400" data-testid="pending-response-loading">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500" data-testid="pending-response-empty">
            No customer messages are waiting on a reply.
          </div>
        ) : (
          <>
            <Table data-testid="pending-response-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  {showWorkshop && <TableHead>Workshop</TableHead>}
                  <TableHead>Advisor</TableHead>
                  <TableHead>Waiting</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((v) => (
                  <TableRow key={v.id} className="cursor-pointer" data-testid={`pending-response-row-${v.id}`} onClick={() => setSelectedId(v.id)}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{v.customerName}</div>
                      <div className="text-xs text-slate-500">{v.vehicleReg}</div>
                    </TableCell>
                    {showWorkshop && <TableCell className="text-slate-700">{v.workshopLocation}</TableCell>}
                    <TableCell className="text-slate-700">{v.advisorName}</TableCell>
                    <TableCell className={`whitespace-nowrap text-slate-600 ${NUM}`}>{formatWaited(v.waitedMins)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination page={page} pageSize={PAGE_SIZE} total={items.length} onPageChange={setPage} />
          </>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="pending-response-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-work">{selected.customerName}</SheetTitle>
                <SheetDescription>
                  {selected.vehicleReg} · {selected.workshopLocation} · {selected.advisorName}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-4 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 border border-amber-200/60">
                  <MessageCircleQuestion size={12} /> Awaiting a reply
                </span>
                <Button size="sm" variant="outline" onClick={() => navigate(`/journey/${selected.id}`)} data-testid="go-to-chat-btn">
                  <MessageCircle size={14} /> Go to chat
                </Button>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-slate-500">
                <div>
                  <div className="text-slate-400">Waiting</div>
                  <div className={`text-slate-700 ${NUM}`}>{formatWaited(selected.waitedMins)}</div>
                </div>
                {selected.lastError && (
                  <div>
                    <div className="text-slate-400">Last error</div>
                    <div className="text-red-600">{selected.lastError}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
